import { useEffect, useMemo, useRef } from "react"

import { AgentMessageType } from "@vertesia/common"

import { MessageItem } from "@components/panels/agent-runner/MessageItem"

import { AgentMessage } from "@models/agents"

interface AllMessagesMixedProps {
    messages: AgentMessage[]
    bottomRef: React.RefObject<HTMLDivElement>
    viewMode?: "agent" | "stacked"
}
export function AllMessagesMixed({ messages, bottomRef, viewMode = "agent" }: Readonly<AllMessagesMixedProps>) {
    const containerRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior: "smooth" })
        }
    }, [messages, bottomRef])

    const displayMessages = useMemo(() => {
        return [...messages].sort((a, b) => {
            const timeA = typeof a.timestamp === "number" ? a.timestamp : new Date(a.timestamp).getTime()
            const timeB = typeof b.timestamp === "number" ? b.timestamp : new Date(b.timestamp).getTime()
            return timeA - timeB
        })
    }, [messages])

    const agentMessages = useMemo(() => {
        return displayMessages.filter((msg) => msg.type === AgentMessageType.ANSWER || msg.type === AgentMessageType.QUESTION || msg.type === AgentMessageType.REQUEST_INPUT)
    }, [displayMessages])

    useEffect(() => {
        const scrollToBottom = () => {
            if (bottomRef.current) {
                bottomRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "end",
                })
            } else if (containerRef.current) {
                containerRef.current.scrollTo({
                    top: containerRef.current.scrollHeight,
                    behavior: "smooth",
                })
            }
        }

        const timeoutId = setTimeout(scrollToBottom, 50)

        return () => clearTimeout(timeoutId)
    }, [messages.length, bottomRef])

    return (
        <div ref={containerRef} className="flex-1 min-h-0 h-full w-full overflow-y-auto flex flex-col relative" data-testid="all-messages-mixed">
            <div className="flex flex-col space-y-2">
                {viewMode === "agent" && (
                    <>
                        {agentMessages.map((message, index) => (
                            <MessageItem key={`${message.timestamp}-${index}`} message={message} />
                        ))}
                    </>
                )}
                {viewMode === "stacked" &&
                    displayMessages.map((message, index) => {
                        return <MessageItem key={`${message.timestamp}-${index}`} message={message} showDetailsOption />
                    })}
                <div ref={bottomRef} className="h-4" aria-hidden="true" />
            </div>
        </div>
    )
}
